import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from "react";
import { useUserContext } from "./UserContext";
import {
    getAllEpmTests,
    getAllEpmTags,
    getAllEpmTestLocations,
    getAllEpmChecklists,
    createEpmTest,
    updateEpmTest,
    getEpmTestById,
    createEpmTag,
    createEpmTestLocation,
    updateEpmTestLocation,
    deleteEpmTestLocation,
    reorderEpmTestLocations,
    createEpmChecklist,
    updateEpmChecklist,
    deleteEpmChecklist,
} from "../utils/APIs";
import { nextTestKey } from "../components/EPM/epmHelpers";

const EPMContext = createContext({});

export const EPMContextProvider = ({ children }) => {
    const { userData } = useUserContext();

    const [tests, setTests] = useState([]);
    const [tagsMap, setTagsMap] = useState({});
    const [testLocations, setTestLocations] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(false);

    const [testChecklistsCache, setTestChecklistsCache] = useState({});
    const testChecklistsCacheRef = useRef({});

    const token = userData?.token;
    const orgToken = userData?.selectedOrganization?.org_token;

    const getEpmData = useCallback(async () => {
        if (!token || !orgToken) return;

        setLoading(true);
        try {
            const [testsRes, tagsRes, locationsRes, checklistsRes] = await Promise.all([
                getAllEpmTests(token, orgToken).catch((err) => {
                    console.warn("Tests not loaded:", err?.message || err);
                    return { data: [] };
                }),
                getAllEpmTags(token, orgToken).catch((err) => {
                    console.warn("Tags not loaded:", err?.message || err);
                    return { data: [] };
                }),
                getAllEpmTestLocations(token, orgToken).catch((err) => {
                    console.warn("Locations not loaded:", err?.message || err);
                    return { data: [] };
                }),
                getAllEpmChecklists(token, orgToken).catch((err) => {
                    console.warn("Checklists not loaded:", err?.message || err);
                    return { data: [] };
                }),
            ]);

            setTests(testsRes?.data ?? []);
            setTestLocations(locationsRes?.data ?? []);
            setChecklists(checklistsRes?.data ?? []);

            const tagMap = {};
            (tagsRes?.data ?? []).forEach((tag) => {
                if (tag?._id) tagMap[tag._id] = tag;
            });
            setTagsMap(tagMap);
        } catch (err) {
            console.error("Error fetching EPM data:", err);
        } finally {
            setLoading(false);
        }
    }, [token, orgToken]);

    useEffect(() => {
        getEpmData();
    }, [getEpmData]);


    useEffect(() => {
        testChecklistsCacheRef.current = testChecklistsCache;

    }, [testChecklistsCache]);

    // ── Create Test ─────────────────────────────────────────────────────────────
    // payload.key is a free-text ID; when blank the server generates the next WEB key.
    const createTest = useCallback(
        async (payload) => {
            if (!token || !orgToken) {
                // Fallback for local preview / unauthenticated dev
                const fallbackTest = {
                    _id: "temp-" + Date.now(),
                    id: "temp-" + Date.now(),
                    ...payload,
                    key: payload.key || nextTestKey(tests),
                    createdAt: new Date().toISOString(),
                };
                setTests((prev) => [fallbackTest, ...(prev || [])]);
                return fallbackTest;
            }

            const res = await createEpmTest(token, orgToken, payload);
            const created = res?.data || res;

            if (created) {
                setTests((prev) => [created, ...(prev || [])]);
                getEpmData();
            }

            return created;
        },
        [token, orgToken, tests, getEpmData],
    );

    // ── Update Test ─────────────────────────────────────────────────────────────
    const updateTest = useCallback(
        async (testId, updatePayload) => {
            if (!testId) return null;

            // Optimistic update
            setTests((prev) =>
                (prev || []).map((t) =>
                    String(t._id || t.id) === String(testId)
                        ? { ...t, ...updatePayload }
                        : t,
                ),
            );

            if (token && orgToken) {
                const res = await updateEpmTest(
                    token,
                    orgToken,
                    testId,
                    updatePayload,
                );
                return res?.data || res;
            }

            return updatePayload;
        },
        [token, orgToken],
    );

    // ── Add Tag to Test ─────────────────────────────────────────────────────────
    const addTagToTest = useCallback(
        async (testId, tagId, currentTagIds) => {
            if (!testId || !tagId) return currentTagIds || [];

            const existingIds =
                currentTagIds ||
                tests.find((t) => String(t._id || t.id) === String(testId))
                    ?.tag_ids ||
                [];

            if (existingIds.includes(tagId)) return existingIds;

            const updatedTagIds = [...existingIds, tagId];
            await updateTest(testId, { tag_ids: updatedTagIds });
            return updatedTagIds;
        },
        [tests, updateTest],
    );

    // ── Remove Tag from Test ────────────────────────────────────────────────────
    const removeTagFromTest = useCallback(
        async (testId, tagId, currentTagIds) => {
            if (!testId || !tagId) return currentTagIds || [];

            const existingIds =
                currentTagIds ||
                tests.find((t) => String(t._id || t.id) === String(testId))
                    ?.tag_ids ||
                [];

            const updatedTagIds = existingIds.filter((id) => id !== tagId);
            await updateTest(testId, { tag_ids: updatedTagIds });
            return updatedTagIds;
        },
        [tests, updateTest],
    );

    // ── Fetch Test by ID ────────────────────────────────────────────────────────
    const fetchTestById = useCallback(
        async (testId) => {
            if (!testId) return null;

            if (token && orgToken) {
                const res = await getEpmTestById(token, orgToken, testId);
                return res?.data || res;
            }

            return (
                tests.find((t) => String(t._id || t.id) === String(testId)) ||
                null
            );
        },
        [token, orgToken],
    );

    // ── Create Tag ──────────────────────────────────────────────────────────────
    const createTag = useCallback(
        async (name) => {
            const trimmedName = name?.trim();
            if (!trimmedName) throw new Error("Tag name is required");

            let newTag;
            if (token && orgToken) {
                const res = await createEpmTag(token, orgToken, {
                    name: trimmedName,
                });
                newTag = res?.data || res;
            } else {
                newTag = {
                    _id: "tag-" + Date.now(),
                    name: trimmedName,
                    createdAt: new Date().toISOString(),
                };
            }

            if (newTag?._id) {
                setTagsMap((prev) => ({
                    ...prev,
                    [newTag._id]: newTag,
                }));
            }
            return newTag;
        },
        [token, orgToken],
    );

    // ── Create and Add Tag to Test ──────────────────────────────────────────────
    const createAndAddTagToTest = useCallback(
        async (testId, tagName, currentTagIds) => {
            const newTag = await createTag(tagName);
            if (newTag?._id && testId) {
                const updatedTagIds = await addTagToTest(
                    testId,
                    newTag._id,
                    currentTagIds,
                );
                return { newTag, updatedTagIds };
            }
            return { newTag, updatedTagIds: currentTagIds || [] };
        },
        [createTag, addTagToTest],
    );

    // ── Test Locations Handlers ──────────────────────────────────────────────────
    const fetchTestLocations = useCallback(
        async (params = {}) => {
            if (!token || !orgToken) return [];
            try {
                const res = await getAllEpmTestLocations(token, orgToken, params);
                const list = res?.data ?? [];
                setTestLocations(list);
                return list;
            } catch (err) {
                console.error("Error fetching test locations:", err);
                throw err;
            }
        },
        [token, orgToken],
    );

    const createTestLocation = useCallback(
        async (payload) => {
            if (!token || !orgToken) {
                const fallback = {
                    _id: "loc-" + Date.now(),
                    ...payload,
                    order: payload.order || testLocations.length + 1,
                    createdAt: new Date().toISOString(),
                };
                setTestLocations((prev) => [...(prev || []), fallback]);
                return fallback;
            }

            const res = await createEpmTestLocation(token, orgToken, payload);
            const created = res?.data || res;
            if (created) {
                await fetchTestLocations();
            }
            return created;
        },
        [token, orgToken, testLocations, fetchTestLocations],
    );

    const updateTestLocation = useCallback(
        async (id, updatePayload) => {
            if (!id) return null;

            if (token && orgToken) {
                const res = await updateEpmTestLocation(
                    token,
                    orgToken,
                    id,
                    updatePayload,
                );
                await fetchTestLocations();
                return res?.data || res;
            }

            setTestLocations((prev) =>
                (prev || []).map((loc) =>
                    String(loc._id || loc.id) === String(id)
                        ? { ...loc, ...updatePayload }
                        : loc,
                ),
            );
            return updatePayload;
        },
        [token, orgToken, fetchTestLocations],
    );

    const deleteTestLocation = useCallback(
        async (id) => {
            if (!id) return null;

            if (token && orgToken) {
                const res = await deleteEpmTestLocation(token, orgToken, id);
                await fetchTestLocations();
                return res?.data || res;
            }

            setTestLocations((prev) =>
                (prev || []).filter((loc) => String(loc._id || loc.id) !== String(id)),
            );
            return { success: true };
        },
        [token, orgToken, fetchTestLocations],
    );

    const reorderTestLocations = useCallback(
        async (reorderPayload) => {
            if (!reorderPayload) return [];

            if (token && orgToken) {
                const res = await reorderEpmTestLocations(
                    token,
                    orgToken,
                    reorderPayload,
                );
                const updated = res?.data ?? [];
                setTestLocations(updated);
                return updated;
            }

            return testLocations;
        },
        [token, orgToken, testLocations],
    );

    // ── Checklists Handlers ──────────────────────────────────────────────────
    const fetchChecklists = useCallback(
        async (params = {}) => {
            if (!token || !orgToken) return [];
            try {
                const res = await getAllEpmChecklists(token, orgToken, params);
                const list = res?.data ?? [];
                setChecklists(list);
                return list;
            } catch (err) {
                console.error("Error fetching checklists:", err);
                throw err;
            }
        },
        [token, orgToken],
    );

    const createChecklist = useCallback(
        async (payload) => {
            if (!token || !orgToken) {
                const fallback = {
                    _id: "chk-" + Date.now(),
                    ...payload,
                    createdAt: new Date().toISOString(),
                };
                setChecklists((prev) => [...(prev || []), fallback]);
                return fallback;
            }

            const res = await createEpmChecklist(token, orgToken, payload);
            const created = res?.data || res;
            if (created) {
                await fetchChecklists();
            }
            return created;
        },
        [token, orgToken, fetchChecklists],
    );

    const updateChecklist = useCallback(
        async (id, updatePayload) => {
            if (!id) return null;

            if (token && orgToken) {
                const res = await updateEpmChecklist(
                    token,
                    orgToken,
                    id,
                    updatePayload,
                );
                await fetchChecklists();
                return res?.data || res;
            }

            setChecklists((prev) =>
                (prev || []).map((chk) =>
                    String(chk._id || chk.id) === String(id)
                        ? { ...chk, ...updatePayload }
                        : chk,
                ),
            );
            return updatePayload;
        },
        [token, orgToken, fetchChecklists],
    );

    const deleteChecklist = useCallback(
        async (id) => {
            if (!id) return null;

            if (token && orgToken) {
                const res = await deleteEpmChecklist(token, orgToken, id);
                await fetchChecklists();
                return res?.data || res;
            }

            setChecklists((prev) =>
                (prev || []).filter((chk) => String(chk._id || chk.id) !== String(id)),
            );
            return { success: true };
        },
        [token, orgToken, fetchChecklists],
    );

    const stagingChecklist = useMemo(() => {
        return (checklists || [])
            .filter((c) => !c.test_id && (c.env || '').toUpperCase() === 'STAGING')
            .map((c) => ({
                id: c._id || c.id,
                label: c.name,
                detail: c.description,
                required: c.isRequired !== undefined ? c.isRequired : true,
                ...c,
            }));
    }, [checklists]);

    const productionChecklist = useMemo(() => {
        return (checklists || [])
            .filter((c) => !c.test_id && (c.env || '').toUpperCase() === 'PRODUCTION')
            .map((c) => ({
                id: c._id || c.id,
                label: c.name,
                detail: c.description,
                required: c.isRequired !== undefined ? c.isRequired : true,
                ...c,
            }));
    }, [checklists]);

    const fetchTestChecklists = useCallback(
        async (testId, force = false) => {
            if (!token || !orgToken || !testId) return [];
            const key = String(testId);

            // If already cached and not forced, return cached list without network request
            if (!force && testChecklistsCacheRef.current[key] !== undefined) {
                return testChecklistsCacheRef.current[key];
            }

            try {
                const res = await getAllEpmChecklists(token, orgToken, { test_id: testId });
                const list = res?.data ?? [];
                testChecklistsCacheRef.current = {
                    ...testChecklistsCacheRef.current,
                    [key]: list,
                };
                setTestChecklistsCache((prev) => ({
                    ...prev,
                    [key]: list,
                }));
                return list;
            } catch (err) {
                console.error("Error fetching test-specific checklists:", err);
                return testChecklistsCacheRef.current[key] || [];
            }
        },
        [token, orgToken]
    );

    return (
        <EPMContext.Provider
            value={{
                tests,
                setTests,
                tagsMap,
                setTagsMap,
                testLocations,
                setTestLocations,
                checklists,
                setChecklists,
                stagingChecklist,
                productionChecklist,
                loading,
                getEpmData,
                refetchTests: getEpmData,
                createTest,
                updateTest,
                addTagToTest,
                removeTagFromTest,
                createTag,
                createAndAddTagToTest,
                fetchTestById,
                fetchTestLocations,
                createTestLocation,
                updateTestLocation,
                deleteTestLocation,
                reorderTestLocations,
                fetchChecklists,
                createChecklist,
                updateChecklist,
                deleteChecklist,
                fetchTestChecklists,
                testChecklistsCache,
            }}
        >
            {children}
        </EPMContext.Provider>
    );
};

export const useEPMContext = () => useContext(EPMContext);

